import {Tag} from '@wandb/weave/common/components/Tags';
import {isVersionAlias} from '@wandb/weave/common/util/artifacts';
import {Link} from '@wandb/weave/common/util/links';
import {Button} from '@wandb/weave/components/Button';
import {usePanelStacksForType} from '@wandb/weave/components/Panel2/availablePanels';
import {
  Panel2Loader,
  PanelComp2,
} from '@wandb/weave/components/Panel2/PanelComp';
import {Tailwind} from '@wandb/weave/components/Tailwind';
import {connectionToNodes, constNode, constNone, Type} from '@wandb/weave/core';
import {formatNumber} from '@wandb/weave/core/util/number';
import {convertBytes, formatRelativeTime} from '@wandb/weave/util';
import React from 'react';
import {CellInfo, Column} from 'react-table';

import {
  ArtifactAlias,
  useAddAliasesMutation,
  useArtifactVersionsTableAliasesQuery,
  useCreateArtifactTagAssignmentsMutation,
  useDeleteAliasesMutation,
  useDeleteArtifactTagAssignmentsMutation,
  useEntityTagsQuery,
} from '../../generated/graphql';
import * as GQLTypes from '../../types/graphql';
import {propagateErrorsContext} from '../../util/errors';
import {AddAliasButton, Alias} from '../ArtifactAlias';
import {Table} from '../panels.domain/artifact/Table';
import {
  AddTagAssignmentButton,
  mapGQLTags,
  ModifiedTag,
} from '../TagAssignment';
import {makeColumnSortMethod} from '../WBReactTable';
import {
  ArtifactVersionsTableData,
  usePaginatedArtifactVersionsTableData,
} from './hooks';
import {
  handleAddAlias,
  handleAddTagAssignment,
  handleDeleteAlias,
  handleRemoveTagAssignment,
} from './utils';

type ArtifactVersionsTableProps = {
  onRowClick: (versionIndex: number) => void;
  artifactRegistry?: boolean;
  artifactCollectionID: string;
  artifactCollectionName: string;
  entityName: string;
  projectName: string;
};

export const ArtifactVersionsTable: React.FC<
  ArtifactVersionsTableProps
> = props => {
  const {
    data,
    loading,
    fetchNextPage,
    hasNextPage,
    setPageSize,
    pageSize,
    metadataKeyTypes,
    historyKeys,
    refetch,
  } = usePaginatedArtifactVersionsTableData({
    artifactCollectionID: props.artifactCollectionID,
    initialPageSize: 10,
  });

  // Alias Logic
  const [deleteAliases] = useDeleteAliasesMutation({
    context: propagateErrorsContext(),
  });
  const [addAliases] = useAddAliasesMutation({
    context: propagateErrorsContext(),
  });

  const {data: aliasesData} = useArtifactVersionsTableAliasesQuery({
    variables: {artifactCollectionID: props.artifactCollectionID},
  });

  const availableAliases = React.useMemo(() => {
    if (aliasesData == null) {
      return [];
    }
    const protectedAliases =
      aliasesData?.artifactCollection?.project?.protectedArtifactAliases?.map(
        alias => {
          return {
            alias: alias.alias ?? '',
            isProtected: true,
          };
        }
      ) ?? [];
    const existingAliases =
      aliasesData?.artifactCollection?.aliases?.edges
        .map(edge => {
          return {
            alias: edge.node?.alias ?? '',
            isProtected: false,
          };
        })
        .filter(
          alias => !protectedAliases.some(a => a.alias === alias.alias)
        ) ?? [];
    return protectedAliases.concat(existingAliases);
  }, [aliasesData]);

  const addAlias = React.useCallback(
    async (alias: string, artifactID: string) => {
      await handleAddAlias(
        alias,
        artifactID,
        props.entityName,
        props.projectName,
        props.artifactCollectionName,
        addAliases,
        refetch
      );
    },
    [
      addAliases,
      props.entityName,
      props.projectName,
      props.artifactCollectionName,
      refetch,
    ]
  );

  const deleteAlias = React.useCallback(
    async (alias: string, artifactID: string) => {
      await handleDeleteAlias(
        alias,
        artifactID,
        props.entityName,
        props.projectName,
        props.artifactCollectionName,
        deleteAliases,
        refetch
      );
    },
    [
      deleteAliases,
      props.entityName,
      props.projectName,
      props.artifactCollectionName,
      refetch,
    ]
  );

  // Tag Assignment Logic
  const [addTagAssignment] = useCreateArtifactTagAssignmentsMutation({
    context: propagateErrorsContext(),
  });
  const [removeTagAssignment] = useDeleteArtifactTagAssignmentsMutation({
    context: propagateErrorsContext(),
  });
  const {data: tagData} = useEntityTagsQuery({
    variables: {
      entityName: props.entityName,
    },
    skip: !props.entityName,
  });

  const allTags = React.useMemo(() => {
    const rawTags =
      tagData?.entity?.tagCategories?.flatMap(tc =>
        connectionToNodes(tc.tags)
      ) ?? [];
    return mapGQLTags(rawTags);
  }, [tagData]);

  const deleteTagAssignment = React.useCallback(
    async (tag: ModifiedTag, artifactID: string) => {
      await handleRemoveTagAssignment(
        tag,
        artifactID,
        removeTagAssignment,
        refetch
      );
    },
    [removeTagAssignment, refetch]
  );

  const createTagAssignment = React.useCallback(
    async (tagInput: GQLTypes.Tag, artifactID: string) => {
      await handleAddTagAssignment(
        tagInput,
        artifactID,
        addTagAssignment,
        refetch
      );
    },
    [addTagAssignment, refetch]
  );

  const tableData = React.useMemo(
    () =>
      data.map(row => ({
        row: {
          ...row,
          ttl: {
            createdAt: row.createdAt,
            isGenerated: row.isGenerated,
            ttlDurationSeconds: row.ttlDurationSeconds,
          },
          aliases: row.aliases.map(alias => {
            return {
              alias,
              isProtected: availableAliases.find(a => a.alias === alias)
                ?.isProtected,
            };
          }),
        },
      })),
    [data, availableAliases]
  );

  const tableColumns = React.useMemo(
    () =>
      artifactVersionTableColumns(
        metadataKeyTypes,
        historyKeys,
        deleteAlias,
        addAlias,
        deleteTagAssignment,
        createTagAssignment,
        availableAliases,
        allTags,
        props.artifactRegistry,
        props.onRowClick
      ),
    [
      props.artifactRegistry,
      props.onRowClick,
      metadataKeyTypes,
      historyKeys,
      deleteAlias,
      addAlias,
      deleteTagAssignment,
      createTagAssignment,
      availableAliases,
      allTags,
    ]
  );

  // only show this on the initial load, otherwise default to table loading visualization
  if (loading && tableData.length === 0) {
    console.log('loading');
    return (
      <Tailwind>
        <div className="scrollbar-none mt-20 flex w-full flex-1 flex-auto flex-col overflow-y-auto rounded border border-moon-250">
          <Panel2Loader />
        </div>
      </Tailwind>
    );
  }

  return (
    <Tailwind>
      <div className="mt-20 flex max-h-[400px] w-full flex-1 flex-auto flex-col overflow-x-auto rounded border border-moon-250">
        <div className="flex-1 overflow-auto">
          <Table
            noSearch={true}
            data={tableData}
            columns={tableColumns}
            pageSize={pageSize}
            onChangePageSize={setPageSize}
            hasNextPage={hasNextPage}
            fetchingNextPage={loading}
            onFetchNextPage={fetchNextPage}
            border="none"
            style={{margin: 0}}
          />
        </div>
      </div>
    </Tailwind>
  );
};

function artifactVersionTableColumns(
  metadataKeyTypes: Record<string, Type>,
  historyMetricsKeys: string[],
  deleteAlias: (alias: string, artifactID: string) => Promise<void>,
  addAlias: (alias: string, artifactID: string) => Promise<void>,
  deleteTagAssignment: (tag: ModifiedTag, artifactID: string) => Promise<void>,
  createTagAssignment: (tag: GQLTypes.Tag, artifactID: string) => Promise<void>,
  availableAliases: Array<
    Pick<ArtifactAlias, 'alias'> & {isProtected?: boolean}
  >,
  allTags: ModifiedTag[],
  artifactRegistry?: boolean,
  onRowClick?: (index: number) => void
) {
  const currentTime = new Date().getTime();

  const cols: Column<ArtifactVersionsTableData>[] = [
    {
      Header: 'Version',
      id: 'versionIndex',
      accessor: 'versionIndex',
      resizable: true,
      Cell: (cellInfo: CellInfo) => {
        return (
          <div className="flex h-full items-center justify-center">
            {cellInfo.value}
          </div>
        );
      },
    },
    {
      Header: 'Aliases',
      id: 'aliases',
      accessor: 'aliases',
      resizable: true,
      Cell: (cellInfo: CellInfo) => {
        return (
          <div
            className="flex h-full items-center overflow-x-auto"
            style={{scrollbarWidth: 'none'}}>
            {cellInfo.value.map((alias: any) => {
              const isSystem = alias === 'latest' || isVersionAlias({alias});
              return (
                <Alias
                  key={alias.alias}
                  alias={{alias: alias.alias}}
                  isProtected={alias.isProtected}
                  onDelete={
                    isSystem
                      ? undefined
                      : () => deleteAlias(alias.alias, cellInfo.original.id)
                  }
                />
              );
            })}
            <div className="visible-on-hover">
              <AddAliasButton
                aliases={cellInfo.value}
                availableAliases={availableAliases}
                onCreate={alias => addAlias(alias, cellInfo.original.id)}
              />
            </div>
          </div>
        );
      },
    },
    {
      Header: 'Tags',
      id: 'tags',
      accessor: 'tags',
      resizable: true,
      Cell: (cellInfo: CellInfo) => {
        return (
          <div
            className="flex h-full items-center overflow-x-auto"
            style={{scrollbarWidth: 'none'}}>
            {cellInfo.value.map((tag: any) => (
              <Tag
                key={tag.name}
                tag={tag}
                noun="tag"
                canDelete={true}
                onDelete={() => deleteTagAssignment(tag, cellInfo.original.id)}
              />
            ))}
            <div className="visible-on-hover">
              <AddTagAssignmentButton
                assignedTags={cellInfo.value}
                availableTags={allTags}
                onCreate={tag => createTagAssignment(tag, cellInfo.original.id)}
              />
            </div>
          </div>
        );
      },
    },
    {
      Header: 'Created',
      id: 'createdAt',
      accessor: 'createdAt',
      resizable: true,
      Cell: (cellInfo: CellInfo) => {
        return (
          <div className="flex h-full items-center justify-center">
            {new Date(cellInfo.value).toDateString()}
          </div>
        );
      },
    },
    {
      Header: 'TTL Remaining',
      id: 'ttlRemaining',
      accessor: 'ttl',
      resizable: true,
      Cell: (cellInfo: CellInfo) => {
        let ttlRemainingText;
        if (cellInfo.value.isGenerated) {
          ttlRemainingText = 'N/A';
        } else if (cellInfo.value.ttlDurationSeconds === -2) {
          ttlRemainingText = 'Inactive';
        } else {
          // add the ttl duration to the createdAt date in milliseconds
          const createdAt = new Date(cellInfo.value.createdAt).getTime();
          const ttlDurationMs = cellInfo.value.ttlDurationSeconds * 1000;
          const expiresAt = createdAt + ttlDurationMs;
          ttlRemainingText =
            expiresAt <= currentTime
              ? 'Pending deletion'
              : formatRelativeTime(expiresAt);
        }
        return (
          <div className="flex h-full items-center justify-center">
            {ttlRemainingText}
          </div>
        );
      },
    },
    {
      Header: '# of Consuming Runs',
      id: 'numConsumingRuns',
      accessor: 'usedCount',
      resizable: true,
      Cell: (cellInfo: CellInfo) => {
        return (
          <div className="flex h-full items-center justify-center">
            {cellInfo.value.toString()}
          </div>
        );
      },
    },
    {
      Header: 'Size',
      id: 'size',
      accessor: 'size',
      resizable: true,
      Cell: (cellInfo: CellInfo) => {
        return (
          <div className="flex h-full items-center justify-center">
            {convertBytes(cellInfo.value)}
          </div>
        );
      },
    },
  ];

  if (onRowClick) {
    cols.splice(0, 0, {
      Header: '',
      id: 'link',
      accessor: 'versionIndex',
      resizable: true,
      Cell: (cellInfo: CellInfo) => {
        return (
          <div className="h-64">
            <Button
              size="small"
              variant="outline"
              onClick={() => {
                onRowClick(cellInfo.value);
              }}>
              View
            </Button>
          </div>
        );
      },
    });
  }

  // We only show the logged by column for non registry collections because checking
  // whether the viewer has access to the source artifact's project for linked artifacts
  // was difficult to do with weave.
  // TODO: Change this to always show the logged by column but condition the cell content
  // based on viewer access.
  if (!artifactRegistry) {
    cols.splice(3, 0, {
      Header: 'Logged By',
      id: 'createdBy',
      accessor: 'createdBy',
      resizable: true,
      Cell: (cellInfo: CellInfo) => {
        let createdByText = 'Unknown';
        let createdByLink = null;
        if (cellInfo.value?.type === 'Run') {
          const {entityName, projectName, name} = cellInfo.value;
          createdByLink = `/${entityName}/${projectName}/runs/${name}`;
          createdByText = cellInfo.value.displayName;
        } else if (cellInfo.value?.type === 'User') {
          createdByLink = `/${cellInfo.value.username}`;
          createdByText = cellInfo.value.name;
        }
        if (!createdByLink) {
          return createdByText;
        }
        return <Link to={createdByLink}>{createdByText}</Link>;
      },
    });
  }

  for (const [key, value] of Object.entries(metadataKeyTypes)) {
    cols.push({
      Header: `m.${key}`,
      id: key,
      accessor: 'metadata',
      resizable: true,
      sortMethod: makeColumnSortMethod(metadata => metadata[key]),
      Cell: (cellInfo: CellInfo) => {
        let inputNode;
        if (cellInfo.value[key] != null) {
          inputNode = constNode(value, cellInfo.value[key]);
        } else {
          inputNode = constNone();
        }

        const {handler} = usePanelStacksForType(value, `metadata-${key}`, {
          excludeTable: true,
          excludePlot: true,
          disallowedPanels: ['Group', 'Expression'],
        });

        if (!handler) {
          return '-';
        }

        return (
          <div className="flex h-full items-center justify-center [&_pre]:font-['Source_Sans_Pro']">
            <PanelComp2
              input={inputNode}
              inputType={value}
              loading={false}
              panelSpec={handler}
              configMode={false}
              context={{}}
              config={{}}
              updateConfig={() => {}}
              updateContext={() => {}}
              updateInput={() => {}}
            />
          </div>
        );
      },
    });
  }

  for (const key of historyMetricsKeys) {
    cols.push({
      Header: `h.${key}`,
      id: key,
      accessor: 'runHistory',
      resizable: true,
      sortMethod: makeColumnSortMethod(history => history[key]),
      Cell: (cellInfo: CellInfo) => {
        return (
          <div className="flex h-full items-center justify-center">
            {formatNumber(cellInfo.value[key] ?? '-', 'Automatic')}
          </div>
        );
      },
    });
  }
  return cols;
}
